// dokumentScanUebernahme.ts — wohin die Werte eines Dokument-Scans in der
// Personen-Anlage gehen.
//
// Zwei Fragen, die `NewPrivateContactForm.applyDateiScan` sonst selbst
// beantworten müsste — hier rein und ohne React, damit sie Tests bekommen:
//
//  1. Welcher DOKUMENTEINTRAG bekommt Nummer, Datum und Behörde? Die Maske
//     führt mehrere Dokumente (Ausweis, Führerschein, Urkunden …). Bis
//     09/2026 landete jeder Scan in `documents[0]` — ein Führerschein-Scan
//     hätte also die Nummer des Personalausweises überschrieben bzw. wäre an
//     dessen belegten Feldern abgeprallt.
//
//  2. Welcher Firma gehören die Kanäle einer VISITENKARTE? Seit 09/2026
//     (Plan „OCR-Scan Kontaktanlage", V3–V6) trennt der Scan Zentrale und
//     Person: Zentrale, info@ und Fax gehen an die Firma selbst; Durchwahl,
//     Handy und persönliche E-Mail bleiben an der Person, sind geschäftlich
//     und tragen den Bezug zur Firma der Karte (P8). Die Anschrift ist die der
//     Firma, nicht die Wohnanschrift.

import type { AusweisdokumentEintrag } from "../components/forms/widgets/AusweisdokumentAbschnitt";
import type { WeitereKommunikationEintrag } from "../components/forms/widgets/WeitereKommunikationDialog";
import { FUEHRERSCHEIN, GEBURTSURKUNDE, HEIRATSURKUNDE, PERSONALAUSWEIS, STERBEURKUNDE } from "../data/ausweisarten";

/** Felder, die an einem Dokumenteintrag hängen statt an der Person. */
export const DOKUMENT_SCHLUESSEL = [
  "idCardType",
  "idCardSubtype",
  "idCardClasses",
  "idCardNumber",
  "idCardIssuedAt",
  "idCardValidUntil",
  "idCardAuthority",
] as const;
type DokSchluessel = (typeof DOKUMENT_SCHLUESSEL)[number];

const DOK_SET = new Set<string>(DOKUMENT_SCHLUESSEL);

/** Scan-Art → Dokumentart. Der Ausweis-Scan nennt seine Art selbst. */
const ART_DER_URKUNDE: Record<string, string> = {
  fuehrerschein: FUEHRERSCHEIN,
  geburtsurkunde: GEBURTSURKUNDE,
  heiratsurkunde: HEIRATSURKUNDE,
  sterbeurkunde: STERBEURKUNDE,
};

const leer = (v: unknown) => String(v ?? "").trim() === "";

/** Ein Eintrag, in dem noch niemand etwas erfasst hat (nur die Vorgabe-Art). */
export const dokumentIstLeer = (d: AusweisdokumentEintrag): boolean =>
  DOKUMENT_SCHLUESSEL.filter((k) => k !== "idCardType").every((k) => leer(d[k]));

export type Verteilung = {
  /** Werte für die Person — ohne Dokumentfelder. */
  personWerte: Record<string, string>;
  /** Die neue Dokumentliste, oder null, wenn der Scan kein Dokument betrifft. */
  docs: AusweisdokumentEintrag[] | null;
  /** Der Eintrag, der gefüllt wurde (für die abzulegende Datei). */
  zielId: string | null;
};

/**
 * Verteilt die Werte eines Scans auf Person und Dokumenteintrag.
 *
 * Regeln:
 *  - Mit `zielId` (Scan-Knopf an einem bestimmten Eintrag) gilt dieser Eintrag.
 *  - Sonst: ein Eintrag derselben Art mit derselben Nummer, dann einer
 *    derselben Art ohne Nummer, dann ein ganz leerer
 *    Eintrag (die Vorgabe „Personalausweis" ohne Angaben), sonst ein neuer.
 *  - Es werden nur LEERE Felder des Eintrags gefüllt — wie in der übrigen
 *    Maske überschreibt ein Scan nie, was schon dasteht.
 *  - Ein Sterbedatum setzt den Schalter „Verstorben" mit.
 */
export function dokumentScanVerteilen(args: {
  werte: Record<string, string>;
  scanArt?: string;
  docs: AusweisdokumentEintrag[];
  zielId?: string | null;
  neueId: () => string;
}): Verteilung {
  const { werte, scanArt, docs, zielId, neueId } = args;
  const personWerte: Record<string, string> = {};
  const dokWerte: Partial<Record<DokSchluessel, string>> = {};
  for (const [k, v] of Object.entries(werte)) {
    const wert = String(v ?? "").trim();
    if (!wert) continue;
    if (DOK_SET.has(k)) dokWerte[k as DokSchluessel] = wert;
    else personWerte[k] = wert;
  }
  if (personWerte.deceasedAt) personWerte.deceased = "ja";

  const urkundenArt = scanArt ? ART_DER_URKUNDE[scanArt] : undefined;
  const hatDokWerte = Object.keys(dokWerte).length > 0;
  if (!urkundenArt && !hatDokWerte) return { personWerte, docs: null, zielId: null };

  const vorgegeben = docs.find((d) => d.id === zielId);
  const art = vorgegeben?.idCardType || urkundenArt || dokWerte.idCardType || PERSONALAUSWEIS;

  let ziel: AusweisdokumentEintrag | undefined = vorgegeben;
  let neu = false;
  if (!ziel) {
    const nummer = dokWerte.idCardNumber;
    ziel =
      // Dasselbe Dokument noch einmal gescannt (etwa die Rückseite nachgereicht)?
      (nummer ? docs.find((d) => d.idCardType === art && d.idCardNumber === nummer) : undefined) ??
      // Nur die Art erkannt, und ein Eintrag dieser Art existiert schon.
      (Object.keys(dokWerte).every((k) => k === "idCardType") && !urkundenArt
        ? docs.find((d) => d.idCardType === art)
        : undefined) ??
      docs.find((d) => d.idCardType === art && leer(d.idCardNumber)) ??
      (docs.length === 1 && dokumentIstLeer(docs[0]) ? docs[0] : undefined);
  }
  if (!ziel) {
    ziel = { id: neueId(), idCardType: art };
    neu = true;
  }

  const gefuellt: AusweisdokumentEintrag = { ...ziel };
  // Die Art wechselt nur an einem leeren Eintrag — nie an einem, in dem schon
  // etwas steht (sonst passten Unterart und Klassen nicht mehr dazu).
  if (!vorgegeben && (neu || dokumentIstLeer(ziel))) gefuellt.idCardType = art;
  for (const k of DOKUMENT_SCHLUESSEL) {
    if (k === "idCardType") continue;
    const wert = dokWerte[k];
    if (wert && leer(gefuellt[k])) gefuellt[k] = wert;
  }

  const naechste = neu ? [...docs, gefuellt] : docs.map((d) => (d.id === gefuellt.id ? gefuellt : d));
  return { personWerte, docs: naechste, zielId: gefuellt.id };
}

// ── Visitenkarte: die Kanäle der Person tragen den Firmenbezug ──────────────

/** Die geschäftlichen Kanäle der Personen-Maske, die eine Karte füllt. */
export const FIRMENKANAELE = ["phoneWork", "mobileWork", "emailWork"] as const;

/** Pseudo-Schlüssel des Mappers für das Fax — die Maske hat kein eigenes Feld. */
export const FAX_SCHLUESSEL = "faxWork";

export type Firma = { id?: number; name: string };

/**
 * Bindet die Kanäle einer Visitenkarte an die Firma der Karte.
 *
 * Liefert die Firmen-Zuordnung je Kanal (`commCompanies` der Maske) — nur für
 * Kanäle, die der Scan tatsächlich füllt und die noch keiner Firma zugeordnet
 * sind — und das Fax als geschäftlichen Eintrag unter „Weitere
 * Kommunikation", ebenfalls mit der Firma.
 */
export function visitenkarteFirmenbezug(args: {
  werte: Record<string, string>;
  firma?: Firma | null;
  /** Füllt der Scan dieses Feld? (Nur leere Felder werden gefüllt.) */
  wirdGefuellt: (key: string) => boolean;
  zugeordnet: Record<string, Firma | undefined>;
  weitere: WeitereKommunikationEintrag[];
  neueId: () => string;
}): {
  werte: Record<string, string>;
  zuordnungen: Record<string, Firma>;
  weitereNeu: WeitereKommunikationEintrag[];
} {
  const { firma, wirdGefuellt, zugeordnet, weitere, neueId } = args;
  const werte = { ...args.werte };
  const fax = (werte[FAX_SCHLUESSEL] ?? "").trim();
  delete werte[FAX_SCHLUESSEL];

  const zuordnungen: Record<string, Firma> = {};
  const weitereNeu: WeitereKommunikationEintrag[] = [];
  const firmaGesetzt = !!firma && firma.name.trim() !== "";

  if (firmaGesetzt) {
    for (const k of FIRMENKANAELE) {
      if (!leer(werte[k]) && wirdGefuellt(k) && !zugeordnet[k]) zuordnungen[k] = firma!;
    }
  }

  if (fax) {
    const schonDa = weitere.some(
      (w) => w.type === "fax" && w.value.replace(/\s+/g, "") === fax.replace(/\s+/g, ""),
    );
    if (!schonDa) {
      weitereNeu.push({
        id: neueId(),
        type: "fax",
        label: "Fax",
        value: fax,
        zuordnung: "geschaeftlich",
        ...(firmaGesetzt ? { companyId: firma!.id, companyName: firma!.name } : {}),
      });
    }
  }

  return { werte, zuordnungen, weitereNeu };
}
